import type { ProjectSummary } from '@shared/api';
import { useQuery } from '@tanstack/react-query';
import { Building2, CheckCircle2, ChevronRight, FolderKanban, Plus, Users } from 'lucide-react';
import { Link } from 'react-router';
import { Button } from '../../components/ui/button';
import { Card, CardBody, CardHeader, Stat } from '../../components/ui/card';
import { Badge, EmptyState, Spinner } from '../../components/ui/feedback';
import { api } from '../../lib/api';
import { queryKeys } from '../../lib/query';
import { useMe } from '../../lib/session';
import { QuickStart } from './quick-start';
import { Templates } from './templates';

function formatNumber(value: number): string {
  return new Intl.NumberFormat().format(value);
}

const STATUS_TONE = {
  completed: 'success',
  running: 'accent',
  queued: 'accent',
  paused: 'warning',
  failed: 'danger',
  cancelled: 'neutral',
  draft: 'neutral',
} as const;

export function DashboardPage() {
  const me = useMe();
  const projects = useQuery({ queryKey: queryKeys.projects, queryFn: () => api.get<ProjectSummary[]>('/api/projects') });

  const totals = (projects.data ?? []).reduce(
    (sum, project) => ({
      accounts: sum.accounts + (project.stats?.accounts ?? 0),
      contactable: sum.contactable + (project.stats?.contactable ?? 0),
      people: sum.people + (project.stats?.people ?? 0),
    }),
    { accounts: 0, contactable: 0, people: 0 },
  );

  return (
    <div className="mx-auto max-w-[1400px] px-5 py-5">
      <header className="mb-5 flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-lg font-semibold tracking-tight text-foreground">Dashboard</h1>
          <p className="mt-0.5 text-[13px] text-muted-foreground">
            {me.data?.workspace.kind === 'demo'
              ? 'This is a demo workspace: sources run against built-in fixture data, never the live internet.'
              : 'Overview of your research projects and the data collected so far.'}
          </p>
        </div>
        <Link to="/research/new">
          <Button icon={<Plus className="h-3.5 w-3.5" />}>New research</Button>
        </Link>
      </header>

      <div className="mb-4 space-y-3">
        <QuickStart />
        <Templates />
      </div>

      <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
        <Stat label="Research projects" value={formatNumber(projects.data?.length ?? 0)} icon={<FolderKanban className="h-4 w-4" />} />
        <Stat label="Businesses" value={formatNumber(totals.accounts)} icon={<Building2 className="h-4 w-4" />} />
        <Stat
          label="Contactable"
          value={formatNumber(totals.contactable)}
          hint={totals.accounts > 0 ? `${Math.round((totals.contactable / totals.accounts) * 100)}% of discovered` : undefined}
          icon={<CheckCircle2 className="h-4 w-4" />}
        />
        <Stat label="People" value={formatNumber(totals.people)} icon={<Users className="h-4 w-4" />} />
      </div>

      <Card className="mt-4">
        <CardHeader title="Recent research" description="Projects in this workspace, most recently updated first." />
        {projects.isPending ? (
          <CardBody className="flex items-center gap-2 text-sm text-muted-foreground">
            <Spinner /> Loading projects…
          </CardBody>
        ) : (projects.data?.length ?? 0) === 0 ? (
          <EmptyState
            icon={<FolderKanban className="h-6 w-6" />}
            title="No research yet"
            description="Once a research project runs, its businesses, contacts and source health appear here."
            action={
              <Link to="/research/new">
                <Button size="sm" variant="outline">
                  Start your first research
                </Button>
              </Link>
            }
          />
        ) : (
          <ul className="divide-y divide-border">
            {projects.data?.map((project) => (
              <li key={project.id}>
                <Link
                  to={`/research/${project.id}`}
                  className="flex items-center justify-between gap-4 px-4 py-2.5 transition-colors duration-150 hover:bg-muted"
                >
                  <div className="min-w-0">
                    <p className="truncate text-sm font-medium text-foreground">{project.name}</p>
                    <p className="mt-0.5 text-[13px] text-muted-foreground">
                      {project.sources.join(', ')}
                      {project.stats ? ` · ${formatNumber(project.stats.accounts)} businesses` : ''}
                    </p>
                  </div>
                  <span className="flex shrink-0 items-center gap-2">
                    <Badge tone={STATUS_TONE[project.status]}>{project.status}</Badge>
                    <ChevronRight className="h-4 w-4 text-muted-foreground" aria-hidden="true" />
                  </span>
                </Link>
              </li>
            ))}
          </ul>
        )}
      </Card>
    </div>
  );
}
